import { Avatar } from '@components/avatar';
import { PushNotifications } from '@components/permisions/pushNotifications';
import { PushTest } from '@components/permisions/test';
import { UpdateSW } from '@components/serviceWorker/update';
import { Button } from '@components/ui/button';
import { NavMenu } from '@components/ui/navigationMenu/navigationMenu';
import { useAppContext } from '@contexts/app';
import { cn } from '@utils/cn';
import { appRoutes } from '@utils/consts';
import { parseRoute } from '@utils/i18n';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, useLocation } from 'react-router';
import { NotificationsDialog } from './notificationsDialog';
import { useGetNotifications } from '@services/notifications';
import { kebabToCamelCase } from '@utils/textHelpers';
import { SvgConsumer } from '@components/icons/svgConsumer';
import { getEnvType } from '@utils/getEnvType';
import { Badge } from '@components/ui/badge';
import { version } from '../../../package.json';
import { OfficeTab } from '@root/globalTypes';
import { icons } from './constants';
import { NavbarButton } from './button';
import { NavbarTooltip } from './tooltip';
import { usePanel } from '@hooks/usePanel';
import { useLogout } from '@hooks/useLogout';
import { ImpersonateBanner } from '@components/ui/impersonateBanner';
import { localStore } from '@utils/localStore';
import { navLinkClasses } from './helpers';
import { Loader } from '@components/loader';
import { useAccess } from '@hooks/access';

export const Navbar = () => {
  const {
    isLogged,
    firstName,
    lastName,
    onDeckError,
    hotSeatError,
    menuAccess: userMenuAccess,
    admin: {
      data: { menuAccess: adminMenuAccess, homePage: adminHomePage, ...adminData },
      isLogged: isAdminLogged,
    },
    homePage: userHomePage,
  } = useAppContext();
  const access = useAccess();

  const admin = isAdminLogged || Boolean(localStore.getItem('app-admin'));
  const user = isLogged || Boolean(localStore.getItem('app-user'));
  const impersonate = Boolean(localStore.getItem('app-impersonate'));
  const impersonateExiting = Boolean(localStore.getItem('app-impersonate-exiting'));

  const { pathname } = useLocation();
  const { panel, parseUrl } = usePanel();
  const { t } = useTranslation('app');
  const logout = useLogout();
  const envType = getEnvType();
  const [notificationsDialog, setNotificationsDialog] = useState(false);
  const { data: notifications } = useGetNotifications();
  const haveNotifications = Boolean(notifications?.records?.length);
  const menuAccess = !user && admin ? adminMenuAccess : userMenuAccess;
  const homePage = parseRoute(`/${!user && admin ? adminHomePage : userHomePage}`);

  const tabError = (path: OfficeTab) => {
    if (path === 'hotSeat' && hotSeatError) return true;
    if (path === 'onDeck' && onDeckError) return true;
    return false;
  };

  const generateLinks = () => {
    if (access.fieldUser) return;
    if (!menuAccess?.length) return <Loader local />;

    return (
      <ul className="absolute left-1/2 flex grow -translate-x-1/2 justify-center gap-4">
        {menuAccess.map((route) => {
          const tab = kebabToCamelCase(route) as OfficeTab;
          return (
            <NavbarTooltip key={route} name={tab}>
              <li>
                <NavLink
                  onClick={(e) => {
                    if (pathname !== `/${parseRoute(route)}`) return;
                    e.preventDefault();
                  }}
                  to={`/${parseRoute(parseUrl(route))}`}
                  className={({ isActive }) => navLinkClasses({ isActive, error: tabError(tab) })}>
                  {icons[tab]}
                </NavLink>
              </li>
            </NavbarTooltip>
          );
        })}
      </ul>
    );
  };

  return (
    <>
      <div className="header sticky left-0 right-0 top-0 z-[60] bg-white shadow-sm">
        <ImpersonateBanner />
        <nav className="header-navbar mx-auto w-full relative flex h-12 items-center gap-5 px-4 py-1">
          <div className="absolute left-4 top-1/2 flex -translate-y-1/2 gap-4">
            <Link className="select-none" to={homePage}>
              <img className="h-5 w-auto" src="/logo.png" />
            </Link>
            {envType !== 'prod' && (
              <Badge variant={envType === 'stage' ? 'pending' : 'active'}>
                {envType} v{version}
              </Badge>
            )}
          </div>
          {(user || admin) && (
            <>
              {generateLinks()}
              <div
                className={`absolute right-4 top-1/2 flex shrink-0 -translate-y-1/2 items-center justify-end gap-2 ${
                  !access.full ? 'ms-auto' : ''
                }`}>
                {/* {!access.fieldUser && (
                <NavbarTooltip name="tutorial">
                  <NavbarButton
                    onClick={() => setData((prev) => ({ ...prev, isTutorial: !prev.isTutorial }))}>
                    <SvgConsumer className="shrink-0 text-heading-s" id="tooltip" />
                  </NavbarButton>
                </NavbarTooltip>
              )} */}
                {access.fieldUser && (
                  <Link to={parseRoute(appRoutes.foremanArchive)}>
                    <NavbarButton
                      className={cn(
                        pathname.includes(appRoutes.foremanArchive) && 'text-primary-500',
                      )}>
                      {Boolean(notifications?.missingOptionalSubtasks) && (
                        <span className="absolute bottom-0.5 right-1 flex h-5 w-5 items-center justify-center rounded-full bg-danger-400 text-white">
                          {notifications?.missingOptionalSubtasks}
                        </span>
                      )}
                      <SvgConsumer className="text-heading-xs" id="archive" />
                    </NavbarButton>
                  </Link>
                )}
                {!admin && (
                  <NavbarTooltip name="notifications">
                    <NavbarButton
                      disabled={!haveNotifications}
                      onClick={() => setNotificationsDialog(!notificationsDialog)}
                      className={cn(
                        Boolean(notifications?.unreadNotifications) &&
                          'after:absolute after:bottom-1 after:right-6 after:block after:h-2.5 after:w-2.5 after:rounded-full after:bg-danger-400',
                      )}>
                      <SvgConsumer className="text-heading-m" id="notifications" />
                    </NavbarButton>
                  </NavbarTooltip>
                )}
                <NavMenu
                  disabled={impersonate}
                  trigger={
                    <Avatar
                      name={[
                        user ? firstName : adminData.firstName,
                        user ? lastName : adminData.lastName,
                      ].join(' ')}
                      hideTooltip
                    />
                  }>
                  <Button
                    disabled={false}
                    variant="ghost"
                    onClick={() => logout(panel ? 'admin' : 'user')}
                    size="sm">
                    {t('navigation.logout')}
                  </Button>
                  <Link to={`/${parseRoute(parseUrl(appRoutes.passwordReset))}`}>
                    <Button
                      disabled={false}
                      className="whitespace-nowrap"
                      variant="ghost"
                      size="sm">
                      {t('navigation.changePassword')}
                    </Button>
                  </Link>
                  <div>{import.meta.env.VITE_PUSH_TEST ? <PushTest /> : null}</div>
                  <div>
                    {import.meta.env.VITE_SENTRY_TEST ? (
                      <button
                        type="button"
                        onClick={() => {
                          throw new Error('Sentry Test Error');
                        }}>
                        Break the world
                      </button>
                    ) : null}
                  </div>
                </NavMenu>
              </div>
            </>
          )}
          {!user && !admin && (
            <Link to={`/${parseRoute(parseUrl(appRoutes.index))}`} className="ms-auto">
              <strong className="font-inter text-s text-neutral-400 hover:text-primary-500">
                {t('navigation.login')}
              </strong>
            </Link>
          )}
        </nav>
        <UpdateSW />
        {access.fieldUser && !impersonate && !impersonateExiting && <PushNotifications />}
        <NotificationsDialog
          open={notificationsDialog}
          setOpen={setNotificationsDialog}
          data={notifications}
        />
      </div>
    </>
  );
};
